<script setup lang="ts">
import VModal from '@shared/component/VModal/VModal.vue';
import { parseDateOnlyAsLocalTime } from '@tools/dates';
import { logger } from '@logging/logging';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { z } from 'zod';
import { computed, ref, toRefs, watch } from 'vue';
import { useId } from '@tools/composables/id';

import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import { usePakketpolisStore } from '../pakketpolisStore';

const props = defineProps<{
  pakketpolisnummer: string;
  polisnummer: string;
}>();
const { pakketpolisnummer, polisnummer } = toRefs(props);

const isOpen = defineModel<boolean>('isOpen');
const ingangsdatumId = useId('nieuweingangsdatum');

const store = usePakketpolisStore();
const pakketpolis = computed(() => store.getPakketpolis(pakketpolisnummer.value));
const subpolis = computed(() =>
  pakketpolis.value?.originelePakketpolis.subpolissen.find(
    (p) => p.polisnummer === polisnummer.value,
  ),
);
const datumRange = computed(() => {
  const sub = pakketpolis.value?.mutatieActies.subpolisMutatieActies.find(
    (s) => s.polisnummer === polisnummer.value,
  );
  return sub?.ingangsdatum.datumRange;
});
const minimaleIngangsdatum = computed(() =>
  parseDateOnlyAsLocalTime(datumRange.value?.minimaleDatum),
);
const maximaleIngangsdatum = computed(() =>
  parseDateOnlyAsLocalTime(datumRange.value?.maximaleDatum),
);

const validationSchema = computed(() =>
  toTypedSchema(
    z.object({
      ingangsdatum: z.date().refine(
        (date) => {
          if (!minimaleIngangsdatum.value || !maximaleIngangsdatum.value) {
            return true;
          }

          return !(date < minimaleIngangsdatum.value || date > maximaleIngangsdatum.value);
        },
        {
          message: `De ingangsdatum moet tussen ${minimaleIngangsdatum.value?.toLocaleDateString('nl-NL')} en ${maximaleIngangsdatum.value?.toLocaleDateString('nl-NL')} liggen.`,
        },
      ),
    }),
  ),
);

const { errors, useFieldModel, validate, resetForm } = useForm({
  validationSchema,
  initialValues: {
    ingangsdatum: subpolis.value?.ingangsdatum
      ? parseDateOnlyAsLocalTime(subpolis.value?.ingangsdatum)
      : new Date(),
  },
});

watch(polisnummer, () => {
  resetForm({
    values: {
      ingangsdatum: subpolis.value?.ingangsdatum
        ? parseDateOnlyAsLocalTime(subpolis.value?.ingangsdatum)
        : new Date(),
    },
  });
});
const ingangsdatum = useFieldModel('ingangsdatum');

async function opslaan() {
  const validationResult = await validate();
  if (!validationResult.valid) {
    logger.debug('validationResult', validationResult.results);
    return;
  }

  store.setSubpolisIngangsdatumGemuteerd(
    pakketpolisnummer.value,
    polisnummer.value,
    ingangsdatum.value!,
  );
  isOpen.value = false;
}

const isValid = ref<boolean>(false);
watch(
  ingangsdatum,
  async () => {
    const result = await validate();
    isValid.value = result.valid;
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <VModal
    overflow
    confirm-button-text="Mutatie opslaan"
    @close-modal="isOpen = false"
    @confirm-action="opslaan()"
    invert-header
    :disabled="!isValid"
    :is-active="isOpen"
    :title="`Mutatie ingangsdatum ${polisnummer}`"
    data-testid="sub-polis-ingangsdatum-modal"
  >
    <FieldRowGroup :gap="2">
      <FieldRowV2
        class="mt-2"
        label-on-own-row
        label="Nieuwe ingangsdatum"
        :label-for="ingangsdatumId"
        :errors="[errors.ingangsdatum]"
      >
        <VDatePicker
          :id="ingangsdatumId"
          v-model="ingangsdatum"
          :invalid="!!errors?.ingangsdatum"
          :clearable="false"
          data-testid="ingangsdatum"
          :min-date="minimaleIngangsdatum ?? ingangsdatum"
          :max-date="maximaleIngangsdatum ?? ingangsdatum"
        />
      </FieldRowV2>
    </FieldRowGroup>
  </VModal>
</template>
